import React, { useState, useCallback, useMemo } from 'react';
import { useCSVReader } from 'react-papaparse';
import { ButtonV2, Flex, TextV2, LinkV2, Divider, Box } from '@withjoy/joykit';
import { ResponsiveValue } from '@withjoy/joykit/styled-system';
import type { Property } from 'csstype';
import { ButtonVariant } from '@withjoy/joykit/components/ButtonV2/Button.types';
import { Download, Import } from '@withjoy/joykit/icons';
import { ResponsiveDialog } from '@apps/card/components/ResponsiveDialog';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import {
  isValidContact,
  isValidPostalCode,
  CONTACT_PROPERTIES_LABEL,
  mapContactsToPayloadData,
  hasAllRequiredColumns,
  mapCSVContactToContact,
  useCreateContacts,
} from './CSVUpload.utils';
import { Table } from './CSVUpload.styles';
import { TelemetryProvider, useCSVUploadTelemetry } from './CSVUpload.telemetry';
import { uniq } from 'lodash-es';

const TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/1eGtdrsJ1iYlnvRYqd2mg8zXT1_wF4EDEs4D_VH1lkSg';
const HELP_DOC_URL = 'https://withjoy.com/help/en/articles/8309207-importing-and-exporting-your-guest-list';

type DialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  context: Context;
  contactCollectionId: string;
  refetchContacts?: () => void;
}>;

const Dialog: React.FC<DialogProps> = ({ isOpen, onClose, context, contactCollectionId, refetchContacts }) => {
  const { trackError, buttonInteracted, trackInvalidPostalCode } = useCSVUploadTelemetry();
  const [step, setStep] = useState<'first' | 'second' | 'third'>('first');
  const [hasParseError, setHasParseError] = useState<boolean>(false);
  const [hasRequiredColumnsError, setHasRequiredColumnsError] = useState<boolean>(false);
  const [validRows, setValidRows] = useState<Contact[]>([]);
  const [invalidRows, setInvalidRows] = useState<Contact[]>([]);
  const [warningRows, setWarningRows] = useState<Contact[]>([]);
  const { eventInfo, eventHandle } = useEventInfo();
  const eventFirebaseId = eventInfo?.eventFirebaseId;
  const eventId = eventInfo?.eventId;
  const { CSVReader } = useCSVReader();
  const { tq } = useTranslation('contactCollector');
  const { createContacts, isCreateContactsLoading, hasCreateContactsError, isGetUserJWTLoading } = useCreateContacts(
    context,
    () => trackError(context, 'Other', { name: 'createContactsError', step }),
    refetchContacts,
  );
  const guestsListUrl = eventHandle ? `/${eventHandle}/edit/guests` : undefined;

  const validRowsLength = validRows.length;
  const invalidRowsLength = invalidRows.length;
  const warningRowsLength = warningRows.length;

  const handleOnImportClick = useCallback(async () => {
    try {
      if (!eventFirebaseId || !eventId) {
        throw Error('No Firebase Event Id or Event Id provided');
      }
      const payload = mapContactsToPayloadData(validRows, contactCollectionId, eventFirebaseId, eventId);
      await createContacts(payload);
      buttonInteracted(context, { name: 'contactsCreated', step, contacts: validRowsLength + invalidRowsLength, valid: validRowsLength, invalid: invalidRowsLength });
    } catch {
      trackError(context, 'Other', { name: 'createContactsNotExecuted', step });
    }
    setStep('third');
  }, [eventFirebaseId, eventId, validRows, contactCollectionId, createContacts, buttonInteracted, context, step, validRowsLength, invalidRowsLength, trackError]);

  const handleOnCloseDialogClick = () => {
    buttonInteracted(context, { name: 'closeDialog', step });
    onClose();
  };

  const handleOnViewContactsClick = useCallback(() => {
    buttonInteracted(context, { name: 'viewContacts', step });
    onClose();
  }, [buttonInteracted, context, onClose, step]);

  const processResults = useCallback(
    (results: CSVResult) => {
      setHasParseError(false);
      setHasRequiredColumnsError(false);
      setValidRows([]);
      setInvalidRows([]);
      const validRows: Contact[] = [];
      const invalidRows: Contact[] = [];
      const warningRows: Contact[] = [];
      const data = results?.data;
      const hasParseError = !!results?.errors?.length;
      setHasParseError(hasParseError);
      if (hasParseError) {
        trackError(context, 'Other', { name: 'parseError', step });
      }
      // validate if some row does not match header columns quantity or there is some separator error - file validation
      if (!hasParseError && data?.length) {
        // validate if all the rows have the required columns - file validation
        if (hasAllRequiredColumns(data)) {
          data.forEach(csvContact => {
            const contact = mapCSVContactToContact(csvContact);
            // validate if the contact is a valid Contact - data validation
            if (isValidContact(contact)) {
              validRows.push(contact);
            } else {
              invalidRows.push(contact);
            }
            if (contact.postalCode && !isValidPostalCode(contact.postalCode, contact.country)) {
              warningRows.push(contact);
              trackInvalidPostalCode(context, { postalCode: contact.postalCode, country: contact.country });
            }
          });
          setValidRows(validRows);
          setInvalidRows(invalidRows);
          setWarningRows(warningRows);
          const validRowsLength = validRows.length;
          const invalidRowsLength = invalidRows.length;
          buttonInteracted(context, { name: 'fileUploaded', step, contacts: validRowsLength + invalidRowsLength, valid: validRowsLength, invalid: invalidRowsLength });
        } else {
          setHasRequiredColumnsError(true);
          trackError(context, 'Other', { name: 'requiredColumnsError', step });
        }
      }
      if (step !== 'second') {
        setStep('second');
      }
    },
    [buttonInteracted, context, step, trackError, trackInvalidPostalCode],
  );

  const renderCSVReader = useMemo(
    () =>
      ({
        startIcon = false,
        buttonLabel,
        variant,
        marginBottom,
      }: {
        startIcon?: boolean;
        buttonLabel: string;
        variant?: ButtonVariant;
        marginBottom?: ResponsiveValue<Property.MarginBottom<string | number>>;
      }) => (
        <CSVReader
          onUploadAccepted={(results: CSVResult) => {
            buttonInteracted(context, { name: buttonLabel, step });
            processResults(results);
          }}
          config={{ header: true, skipEmptyLines: 'greedy' }}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {({ getRootProps }: any) => (
            <ButtonV2
              startIcon={startIcon ? <Import /> : undefined}
              variant={variant}
              marginBottom={marginBottom}
              intent="neutral"
              shape="rounded"
              width="350px"
              {...getRootProps()}
            >
              {buttonLabel}
            </ButtonV2>
          )}
        </CSVReader>
      ),
    [CSVReader, buttonInteracted, context, processResults, step],
  );

  const FirstStepUI = useMemo(
    () => (
      <Flex flexDirection="column">
        <TextV2
          typographyVariant="body1"
          color="mono12"
          marginTop="8px"
        >
          {tq`Start by`}
          <LinkV2
            typographyVariant="body1"
            color="mono12"
            paddingX={1}
            href={TEMPLATE_URL}
            onClick={() => buttonInteracted(context, { name: 'downloadTemplate', step })}
            isExternal
          >
            {tq`downloading the template`}
          </LinkV2>
          {tq`and following the`}
          <LinkV2
            typographyVariant="body1"
            color="mono12"
            paddingX={1}
            href={HELP_DOC_URL}
            onClick={() => buttonInteracted(context, { name: 'helpDoc', step })}
            isExternal
          >
            {tq`formatting instructions.`}
          </LinkV2>
        </TextV2>
        <TextV2
          typographyVariant="body1"
          color="mono12"
          marginTop="16px"
          marginBottom="48px"
        >
          {tq`Note: Every row creates a new contact. Update existing contacts in your`}
          <LinkV2
            typographyVariant="body1"
            color="mono12"
            paddingX={1}
            href={guestsListUrl}
            onClick={() => buttonInteracted(context, { name: 'guestList', step })}
            isExternal
          >
            {tq`Guest List`}
          </LinkV2>
          {tq`to avoid duplicates.`}
        </TextV2>
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <ButtonV2
            startIcon={<Download />}
            intent="neutral"
            variant="outline"
            shape="rounded"
            marginBottom="16px"
            width="350px"
            as="a"
            target="_blank"
            href={TEMPLATE_URL}
            onClick={() => buttonInteracted(context, { name: 'downloadTemplate', step })}
          >
            {tq`Download Template`}
          </ButtonV2>
          {renderCSVReader({ startIcon: true, buttonLabel: tq`Upload File` })}
        </Flex>
      </Flex>
    ),
    [buttonInteracted, context, guestsListUrl, renderCSVReader, step, tq],
  );

  const SecondStepUI = useMemo(() => {
    const concatArr = invalidRows.concat(warningRows);
    const invalidRowsAndWarningRows = uniq(concatArr);
    const invalidRowsAndWarningRowsLength = invalidRowsAndWarningRows.length;

    return (
      <Flex flexDirection="column">
        {hasParseError && (
          <TextV2
            typographyVariant="body1"
            color="mono12"
            marginBottom="16px"
          >
            {tq`The spreadsheet couldn't be uploaded due to incorrect formatting.`}
          </TextV2>
        )}
        {hasRequiredColumnsError && (
          <TextV2
            typographyVariant="body1"
            color="mono12"
            marginBottom="16px"
          >
            {tq`The spreadsheet couldn't have been uploaded due to the required column missing.`}
          </TextV2>
        )}
        {!hasParseError && !hasRequiredColumnsError && (
          <Flex
            alignItems="center"
            justifyContent="center"
          >
            <Flex
              justifyContent="space-around"
              marginBottom="24px"
              width="300px"
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <TextV2
                  typographyVariant="hed4"
                  color="mono12"
                  fontWeight={600}
                >
                  {validRowsLength + invalidRowsLength}
                </TextV2>
                <TextV2
                  typographyVariant="body1"
                  color="mono12"
                >
                  {tq`contacts`}
                </TextV2>
              </Flex>
              <Divider orientation="vertical" />
              <Flex
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <TextV2
                  typographyVariant="hed4"
                  color="green7"
                  fontWeight={600}
                >
                  {validRowsLength}
                </TextV2>
                <TextV2
                  typographyVariant="body1"
                  color="green7"
                >
                  {tq`valid`}
                </TextV2>
              </Flex>
              <Divider orientation="vertical" />
              <Flex
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <TextV2
                  typographyVariant="hed4"
                  color="negative6"
                  fontWeight={600}
                >
                  {invalidRowsLength}
                </TextV2>
                <TextV2
                  typographyVariant="body1"
                  color="negative6"
                >
                  {tq`invalid`}
                </TextV2>
              </Flex>
              <Divider orientation="vertical" />
              <Flex
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <TextV2
                  typographyVariant="hed4"
                  color="warning6"
                  fontWeight={600}
                >
                  {warningRowsLength}
                </TextV2>
                <TextV2
                  typographyVariant="body1"
                  color="warning6"
                >
                  {tq`warning`}
                </TextV2>
              </Flex>
            </Flex>
          </Flex>
        )}
        {!!invalidRowsAndWarningRowsLength && (
          <Box
            marginTop="8px"
            marginBottom="32px"
            maxHeight="200px"
            maxWidth="560px"
            overflow="scroll"
            border="0.5px solid"
            borderColor="mono5"
          >
            <Table>
              <tbody>
                {invalidRowsAndWarningRows.map(
                  ({ firstName, lastName, email, phoneNumber, householdDisplayName, party, address1, address2, city, region, postalCode, country, tags }, index) => (
                    <tr key={`${firstName},${lastName},${index}`}>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color={firstName ? 'mono14' : 'negative6'}
                        >
                          {firstName || tq`Missing ${CONTACT_PROPERTIES_LABEL.firstName}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color={lastName ? 'mono14' : 'negative6'}
                        >
                          {lastName || tq`Missing ${CONTACT_PROPERTIES_LABEL.lastName}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {email || tq`Missing ${CONTACT_PROPERTIES_LABEL.email}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {phoneNumber || tq`Missing ${CONTACT_PROPERTIES_LABEL.phoneNumber}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {householdDisplayName || tq`Missing ${CONTACT_PROPERTIES_LABEL.householdDisplayName}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {party || tq`Missing ${CONTACT_PROPERTIES_LABEL.party}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {address1 || tq`Missing ${CONTACT_PROPERTIES_LABEL.address1}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {address2 || tq`Missing ${CONTACT_PROPERTIES_LABEL.address2}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {city || tq`Missing ${CONTACT_PROPERTIES_LABEL.city}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {region || tq`Missing ${CONTACT_PROPERTIES_LABEL.region}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color={postalCode && !isValidPostalCode(postalCode, country) ? 'warning6' : 'mono14'}
                        >
                          {postalCode && !isValidPostalCode(postalCode, country)
                            ? tq`Invalid Postal Code: ${postalCode}`
                            : !postalCode
                              ? tq`Missing ${CONTACT_PROPERTIES_LABEL.postalCode}`
                              : postalCode}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {country || tq`Missing ${CONTACT_PROPERTIES_LABEL.country}`}
                        </TextV2>
                      </td>
                      <td>
                        <TextV2
                          typographyVariant="body1"
                          color="mono12"
                        >
                          {tags || tq`Missing ${CONTACT_PROPERTIES_LABEL.tags}`}
                        </TextV2>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </Table>
          </Box>
        )}
        <TextV2
          typographyVariant="body1"
          color="mono12"
        >
          {tq`Please make sure that all the required fields are filled in as per spreadsheet`}
          <LinkV2
            typographyVariant="body1"
            color="mono12"
            paddingX={1}
            href={TEMPLATE_URL}
            onClick={() => buttonInteracted(context, { name: 'downloadTemplate', step })}
            isExternal
            _activeLink={{ color: 'mono14' }}
            _visited={{ color: 'mono14' }}
            _active={{ color: 'mono14' }}
            _pressed={{ color: 'mono14' }}
            _hover={{ color: 'mono14' }}
          >
            {tq`template`}
          </LinkV2>
          {tq`and follow the instructions on`}
          <LinkV2
            typographyVariant="body1"
            color="mono12"
            paddingX={1}
            href={HELP_DOC_URL}
            onClick={() => buttonInteracted(context, { name: 'helpDoc', step })}
            isExternal
            _activeLink={{ color: 'mono14' }}
            _visited={{ color: 'mono14' }}
            _active={{ color: 'mono14' }}
            _pressed={{ color: 'mono14' }}
            _hover={{ color: 'mono14' }}
          >
            {tq`this page.`}
          </LinkV2>
        </TextV2>
        <TextV2
          typographyVariant="body1"
          color="mono12"
          marginTop="16px"
          marginBottom="32px"
        >
          <b>{tq`Important:`}</b>{' '}
          {tq`Uploading a spreadsheet will create a new contact in your Guest List for each row. If you already have contacts in your guest list, we recommend editing them directly in your recipient selector or`}
          <LinkV2
            typographyVariant="body1"
            color="mono12"
            paddingX={1}
            href={guestsListUrl}
            onClick={() => buttonInteracted(context, { name: 'guestList', step })}
            isExternal
            _activeLink={{ color: 'mono14' }}
            _visited={{ color: 'mono14' }}
            _active={{ color: 'mono14' }}
            _pressed={{ color: 'mono14' }}
            _hover={{ color: 'mono14' }}
          >
            {tq`Guest List`}
          </LinkV2>
          {tq`to avoid duplicates.`}
        </TextV2>
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          {renderCSVReader({ buttonLabel: tq`Upload New File`, variant: 'outline', marginBottom: '16px' })}
          {!hasParseError && !hasRequiredColumnsError && (
            <ButtonV2
              startIcon={<Import />}
              intent="neutral"
              shape="rounded"
              width="350px"
              onClick={handleOnImportClick}
              loading={isCreateContactsLoading}
              disabled={!validRowsLength || isGetUserJWTLoading}
            >
              {tq`Import Valid Contacts`}
            </ButtonV2>
          )}
        </Flex>
      </Flex>
    );
  }, [
    buttonInteracted,
    context,
    guestsListUrl,
    handleOnImportClick,
    hasParseError,
    hasRequiredColumnsError,
    invalidRows,
    invalidRowsLength,
    isCreateContactsLoading,
    renderCSVReader,
    step,
    tq,
    validRowsLength,
    warningRows,
    warningRowsLength,
    isGetUserJWTLoading,
  ]);

  const ThirdStepUI = useMemo(
    () => (
      <Flex flexDirection="column">
        {hasCreateContactsError ? (
          <>
            <TextV2
              typographyVariant="body1"
              color="mono12"
              marginY="32px"
            >
              {tq`Seems like something went wrong. Please try to submit your spreadsheet again.`}
            </TextV2>
            <Flex
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              {renderCSVReader({ buttonLabel: tq`Upload New File`, marginBottom: '16px' })}
            </Flex>
          </>
        ) : (
          <>
            <TextV2
              typographyVariant="body1"
              color="mono12"
              marginY="32px"
            >
              {tq`We've successfully imported ${validRowsLength} contacts from the spreadsheet.`}
            </TextV2>
            <Flex
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              <ButtonV2
                intent="neutral"
                shape="rounded"
                onClick={handleOnViewContactsClick}
                width="350px"
              >
                {tq`View Contacts`}
              </ButtonV2>
            </Flex>
          </>
        )}
      </Flex>
    ),
    [hasCreateContactsError, tq, renderCSVReader, validRowsLength, handleOnViewContactsClick],
  );

  const StepUI = { first: FirstStepUI, second: SecondStepUI, third: ThirdStepUI };

  const renderHeader = () => {
    if (step === 'third') {
      return hasCreateContactsError ? (
        <TextV2
          typographyVariant="hed5"
          textAlign="center"
        >{tq`Something went wrong`}</TextV2>
      ) : (
        <TextV2
          typographyVariant="hed5"
          textAlign="center"
        >{tq`Success! Upload Completed!`}</TextV2>
      );
    }
    return (
      <TextV2
        typographyVariant="hed5"
        textAlign="center"
      >{tq`Upload Spreadsheet`}</TextV2>
    );
  };

  return (
    <ResponsiveDialog
      desktopSize="lg"
      isOpen={isOpen}
      onClose={handleOnCloseDialogClick}
      header={renderHeader}
      content={() => StepUI[step]}
    />
  );
};

type CSVUploadProps = Readonly<{
  context: Context;
  contactCollectionId: string;
  refetchContacts?: () => void;
}>;

const CSVUpload: React.FC<CSVUploadProps> = ({ context, contactCollectionId, refetchContacts }) => {
  const { buttonInteracted } = useCSVUploadTelemetry();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { tq } = useTranslation('contactCollector');
  const onOpen = useCallback(() => {
    setIsOpen(true);
    buttonInteracted(context, { name: 'openDialog' });
  }, [context, buttonInteracted]);
  const onClose = () => setIsOpen(false);

  const Print = useMemo(
    () => (
      <ButtonV2
        startIcon={<Import />}
        intent="neutral"
        shape="rounded"
        variant="outline"
        fullWidth
        onClick={onOpen}
      >
        {tq`Upload Spreadsheet`}
      </ButtonV2>
    ),
    [onOpen, tq],
  );

  const ContactCollector = useMemo(
    () => (
      <Flex
        padding={6}
        boxShadow="0 3px 8px 0 rgba(0, 0, 0, 0.03), 0 10px 60px -20px rgba(44, 41, 37, 0.03), 0 9px 36px -30px rgba(0, 0, 0, 0.04)"
        borderRadius="12px"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
      >
        <TextV2
          typographyVariant="hed2"
          color="mono14"
          fontWeight={600}
        >
          {tq`Have a contact list already?`}
        </TextV2>
        <ButtonV2
          marginTop="8px"
          startIcon={<Import />}
          intent="neutral"
          variant="ghost"
          shape="rounded"
          onClick={onOpen}
        >
          {tq`Upload Spreadsheet`}
        </ButtonV2>
      </Flex>
    ),
    [onOpen, tq],
  );

  const GuestList = useMemo(
    () => (
      <ButtonV2
        intent="neutral"
        variant="ghost"
        shape="rounded"
        fullWidth
        onClick={onOpen}
      >
        {tq`Learn More`}
      </ButtonV2>
    ),
    [onOpen, tq],
  );

  const Trigger = {
    print: Print,
    contactCollector: ContactCollector,
    guestList: GuestList,
  };

  return (
    <>
      {isOpen && (
        <Dialog
          isOpen={isOpen}
          onClose={onClose}
          context={context}
          contactCollectionId={contactCollectionId}
          refetchContacts={refetchContacts}
        />
      )}
      {Trigger[context]}
    </>
  );
};

export const Root: React.FC<CSVUploadProps> = props => (
  <TelemetryProvider>
    <CSVUpload {...props} />
  </TelemetryProvider>
);
